import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Segment, Image, Dropdown } from 'semantic-ui-react';
import { history, store } from '../../helpers';
import Strings from '../../common/Strings';
import { PageNotFound } from '../PageNotFound';
import Constant from '../../common/Constant';
import { employeeDetailsActions } from '../../actions/employeeDetails';
import {
  dropSecondsAndMillisFromWorkLogMillis,
  getHoursFromMillis,
  getLoggedTimeFromHours,
} from '../../helpers/workLog';
import { HourScatterChart } from '../common/HourScatterChart';
import '../Common.css';
import './EmployeeDetails.css';
import '../common/HourScatterChart.css';
import { exportTeamMemberCsv } from '../../helpers/export';

const tinycolor = require('tinycolor2');

const moment = require('moment');

class EmployeeDetails extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      user: null,
      graphDate: Strings.COMMON.lifetime,
      selectedProjects: null,
    };

    if (!props.isProjectsLoading && !props.isTeamMembersLoading) {
      this.updateUserFromStore(true);
    }
  }

  componentWillReceiveProps(nextProps) {
    if (!nextProps.isProjectsLoading && !nextProps.isTeamMembersLoading) {
      this.updateUserFromStore();
    }
  }

  componentWillUnmount() {
    employeeDetailsActions.stopSync();
  }

  updateUserFromStore(isContructor) {
    const userId = history.location.pathname.replace('/directory/', '');
    const user = store.getState().teamMembers.find(t => t.uid === userId);

    // Initial data
    if (!this.state.user && user) {
      this.props.dispatch(
        employeeDetailsActions.fetchWorkLogs(userId, this.state.graphDate),
      );
    }

    // Update selected members only from initial props
    let selectedProjects;
    if (this.state.selectedProjects) {
      selectedProjects = this.state.selectedProjects;
    } else {
      selectedProjects = [];
      const { projects } = this.props;
      if (projects) {
        projects.forEach(p => selectedProjects.push(p.id));
      }
    }

    if (isContructor) {
      this.state = {
        user,
        graphDate: Strings.COMMON.lifetime,
        selectedProjects,
      };
    } else {
      this.setState({
        user,
        selectedProjects,
      });
    }
  }

  onClickDirectory = () => {
    history.replace('/directory');
  };

  onClickEdit = () => {
    history.push('#editTeamMember', { teamMember: this.state.user });
  };

  onClickExport = (projectsMap, workLogs) => {
    if (workLogs.length === 0) return;
    const { user } = this.state;
    const name = user.name ? user.name : user.email;
    exportTeamMemberCsv(name, projectsMap, workLogs);
  };

  getGraphStartDateAndLoggedProjects() {
    const { workLogs, projects } = this.props;

    let lifetimeStartDate = moment();
    projects.forEach(p => {
      const startMonth = moment(p.createdAt).startOf('month');
      if (startMonth.isBefore(lifetimeStartDate)) {
        lifetimeStartDate = startMonth;
      }
    });

    const loggedProjects = [];
    const loggedProjectsMap = new Map();

    // Populate projects based on all work logs after min start date
    const workLogsAfterMinStartDate = workLogs.filter(w =>
      moment(w.date)
        .startOf('month')
        .isSameOrAfter(moment(lifetimeStartDate).startOf('month')),
    );
    workLogsAfterMinStartDate.forEach(w => {
      if (!loggedProjectsMap.has(w.projectId)) {
        loggedProjectsMap.set(w.projectId, {
          workLog: w,
          totalLoggedMillis: w.totalLoggedMillis,
        });
      } else {
        const newTotalLoggedMillis =
          loggedProjectsMap.get(w.projectId).totalLoggedMillis +
          w.totalLoggedMillis;
        loggedProjectsMap.set(w.projectId, {
          workLog: w,
          totalLoggedMillis: newTotalLoggedMillis,
        });
      }
    });

    // Only populate positive total logged hours
    loggedProjectsMap.forEach(value => {
      if (value.totalLoggedMillis > 0) {
        const p = projects.find(p => p.id === value.workLog.projectId);
        if (p) {
          loggedProjects.push(p.id);
        }
      }
    });

    return { lifetimeStartDate, loggedProjects, loggedProjectsMap };
  }

  renderEmployeeDetails(
    projectsMap,
    filteredWorkLogs,
    lifetimeStartDate,
    loggedProjects,
  ) {
    if (!this.state.user) {
      return null;
    }

    const color = '#4A90E2';
    const colorWithAlpha = tinycolor(color)
      .setAlpha(0.2)
      .toRgbString();

    const { projects } = this.props;
    const { user, graphDate } = this.state;
    let { selectedProjects } = this.state;

    const projectRatesMap = new Map();
    projects.forEach(p => {
      const teamMember = p.teamMembers.find(t => t.uid === user.uid);
      if (teamMember) {
        projectRatesMap.set(p.id, teamMember.hourlyRate);
      }
    });

    const labels = [];
    const chartData = [];
    let chartYMax = 8.0;
    let chartXMax = 1;
    let totalLoggedHours = 0.0;
    let totalCost = 0.0;

    // Populate work logs into dates map
    const workLogMap = new Map();
    filteredWorkLogs.forEach(w => {
      const millisToUse = dropSecondsAndMillisFromWorkLogMillis(
        w.totalLoggedMillis,
      );
      const hoursToUse = getHoursFromMillis(millisToUse);
      if (workLogMap.has(w.date)) {
        const newTotalLoggedMillis = workLogMap.get(w.date) + millisToUse;
        workLogMap.set(w.date, newTotalLoggedMillis);
      } else {
        workLogMap.set(w.date, millisToUse);
      }

      totalLoggedHours += hoursToUse;

      // Increase total cost
      const project = projectsMap.get(w.projectId);
      if (project) {
        const hourlyRate = projectRatesMap.get(project.id);
        if (hourlyRate) {
          totalCost += hoursToUse * hourlyRate;
        }
      }
    });

    let startDate;
    let endDate;
    if (graphDate === Strings.COMMON.lifetime) {
      startDate = moment(lifetimeStartDate).startOf('month');
      endDate = moment().endOf('month');
    } else {
      startDate = moment(graphDate).startOf('month');
      endDate = moment(graphDate).endOf('month');
    }

    while (startDate.isSameOrBefore(endDate)) {
      labels.push(startDate.format('MMM D, YYYY'));
      const loggedMillis = workLogMap.get(
        startDate.format(Constant.DATE_FORMAT),
      );
      if (loggedMillis) {
        const hours = getHoursFromMillis(loggedMillis);
        chartData.push({ x: chartXMax, y: hours });
        if (hours > chartYMax) {
          chartYMax = hours;
        }
      } else {
        chartData.push({ x: chartXMax, y: 0 });
      }

      chartXMax++;
      startDate = startDate.add(1, 'days');
    }

    const graphData = {
      datasets: [
        {
          fill: true,
          borderColor: colorWithAlpha,
          borderWidth: 1,
          showLine: true,
          backgroundColor: colorWithAlpha,
          pointBorderColor: color,
          pointBackgroundColor: color,
          pointHoverRadius: 4,
          pointHoverBackgroundColor: color,
          pointHoverBorderColor: color,
          pointRadius: 2,
          pointHitRadius: 8,
          data: chartData,
        },
      ],
    };

    const ActiveProjects = loggedProjects.map(projectId => {
      const selected = selectedProjects.find(id => id === projectId);
      let container;
      let text;
      if (selected) {
        container = 'employee-details-team-member-container-selected';
        text = 'employee-details-team-member-selected';
      } else {
        container = 'employee-details-team-member-container';
        text = 'employee-details-team-member';
      }
      const displayName = projects.find(p => p.id === projectId).name;
      return (
        <div
          className={container}
          onClick={() => {
            if (selected) {
              selectedProjects = selectedProjects.filter(
                id => id !== projectId,
              );
            } else {
              selectedProjects.push(projectId);
            }
            this.setState({ selectedProjects });
          }}
        >
          <p className={text}>{displayName}</p>
        </div>
      );
    });

    const activeProjectsCount = loggedProjects.filter(projectId =>
      selectedProjects.find(id => id === projectId),
    ).length;

    const stats = [
      {
        label: Strings.EMPLOYEE_DETAILS.activeProjects,
        value: activeProjectsCount,
      },
      {
        label: Strings.COMMON.totalHours,
        value: getLoggedTimeFromHours(totalLoggedHours),
      },
      {
        label: Strings.COMMON.totalCost,
        totalCost,
        currency: store.getState().user.defaultCurrency,
      },
    ];

    return (
      <div className="employee-details-container">
        <div className="employee-details-base-info-container">
          <div style={{ flexGrow: 1 }}>
            <p className="employee-details-base-info-title">
              {Strings.EMPLOYEE_DETAILS.role}
            </p>
            <p className="employee-details-base-info-value">{user.role}</p>
          </div>
          <div>
            <p className="employee-details-base-info-title">
              {Strings.EMPLOYEE_DETAILS.defaultRate}
            </p>
            <div style={{ display: 'flex', flexDirection: 'row' }}>
              <p className="employee-details-base-info-value">
                {new Intl.NumberFormat('en-US', {
                  style: 'currency',
                  currency: store.getState().user.defaultCurrency,
                }).format(user.hourlyRate)}
              </p>
              <p className="employee-details-base-info-per-hour">
                {Strings.COMMON.perHour}
              </p>
            </div>
          </div>
          <p className="employee-details-base-info-rate">
            {Strings.EMPLOYEE_DETAILS.rateInfo}
          </p>
        </div>
        <HourScatterChart
          data={graphData}
          labels={labels}
          chartXMax={chartXMax}
          chartYMax={chartYMax}
          stats={stats}
          statsColor={color}
        />
        <p className="employee-details-team-text">
          {Strings.EMPLOYEE_DETAILS.projects}
        </p>
        <div className="employee-details-team-container">{ActiveProjects}</div>
        <Image className="app-footer-logo" src="/circles-grey.png" />
      </div>
    );
  }

  renderSidePanelContent() {
    const { projects } = this.props;
    const { user } = this.state;

    const activeProjects = [];
    const pendingProjects = [];
    const closedProjects = [];

    projects.forEach(p => {
      const teamMember = p.teamMembers.find(t => t.email === user.email);
      if (teamMember) {
        if (teamMember.status === 'invited') {
          pendingProjects.push(p);
        } else if (p.status === 'closed') {
          closedProjects.push(p);
        } else if (teamMember.status === 'active' && p.status === 'active') {
          activeProjects.push(p);
        }
      }
    });

    let ActiveProjects;
    if (activeProjects.length > 0) {
      ActiveProjects = (
        <div className="employee-details-side-panel-projects">
          {activeProjects.map(p => (
            <div className="employee-details-side-panel-project">
              <div
                className="employee-details-side-panel-project-color"
                style={{ backgroundColor: p.color }}
              />
              <p className="employee-details-side-panel-project-name">
                {p.name}
              </p>
            </div>
          ))}
        </div>
      );
    }

    let PendingProjects;
    if (pendingProjects.length > 0) {
      PendingProjects = (
        <div style={{ marginBottom: '2rem' }}>
          <p className="employee-details-side-panel-header">
            {Strings.EMPLOYEE_DETAILS.pendingProjects}
          </p>
          {pendingProjects.map(p => (
            <div className="employee-details-side-panel-project">
              <div
                className="employee-details-side-panel-project-color"
                style={{ backgroundColor: p.color }}
              />
              <p className="employee-details-side-panel-project-name">
                {p.name}
              </p>
            </div>
          ))}
        </div>
      );
    }

    let ClosedProjects;
    if (closedProjects.length > 0) {
      ClosedProjects = (
        <div>
          <p className="employee-details-side-panel-header">
            {Strings.EMPLOYEE_DETAILS.closedProjects}
          </p>
          {closedProjects.map(p => (
            <div className="employee-details-side-panel-project">
              <p className="employee-details-side-panel-project-name-closed">
                {p.name}
              </p>
            </div>
          ))}
        </div>
      );
    }

    const iworkieId = (
      <div>
        <p
          className="employee-details-side-panel-header"
          style={{ marginBottom: 0 }}
        >
          {Strings.EMPLOYEE_DETAILS.iworkieId}
        </p>
        <p className="employee-details-side-panel-email">{user.email}</p>
      </div>
    );

    return (
      <div>
        {ActiveProjects}
        {PendingProjects}
        {ClosedProjects}
        {iworkieId}
      </div>
    );
  }

  getDropDownData(lifetime, lifetimeStartDate, graphDate) {
    const firstWorkLogDate = moment(lifetimeStartDate).startOf('month');
    let now = moment().startOf('month');
    const months = [lifetime, now.format(Constant.DATE_FORMAT)];
    let monthsDiff = Math.ceil(now.diff(firstWorkLogDate, 'months', true));
    while (monthsDiff > 0) {
      now = now.add(-1, 'months');
      monthsDiff--;
      months.push(now.format(Constant.DATE_FORMAT));
    }
    const dropDownText =
      graphDate === lifetime ? lifetime : moment(graphDate).format('MMMM YYYY');

    return {
      months,
      dropDownText,
    };
  }

  render() {
    const {
      isProjectsLoading,
      isTeamMembersLoading,
      projects,
      workLogs,
      dispatch,
    } = this.props;
    if (isProjectsLoading || isTeamMembersLoading) {
      return null;
    }

    const { user, graphDate, selectedProjects } = this.state;
    if (!user) {
      return <PageNotFound />;
    }

    const {
      lifetimeStartDate,
      loggedProjects,
    } = this.getGraphStartDateAndLoggedProjects();

    const lifetime = Strings.COMMON.lifetime;
    const { months, dropDownText } = this.getDropDownData(
      lifetime,
      lifetimeStartDate,
      graphDate,
    );

    const projectsMap = new Map();
    projects.forEach(p => projectsMap.set(p.id, p));
    loggedProjects.sort((a, b) =>
      projectsMap.get(a).name.localeCompare(projectsMap.get(b).name),
    );

    const filteredWorkLogs = workLogs.filter(
      w =>
        moment(w.date)
          .startOf('month')
          .isSameOrAfter(moment(lifetimeStartDate).startOf('month')) &&
        selectedProjects.find(id => id === w.projectId),
    );

    return (
      <Segment basic className="employee-details-root">
        <Segment basic className="employee-details">
          <div className="common-top-panel">
            <div className="employee-details-title-container">
              <div className="employee-details-title-top-container">
                <p id="employee-details-title" onClick={this.onClickDirectory}>
                  {Strings.EMPLOYEE_DETAILS.directory}
                </p>
                <p className="employee-details-title-slash">/</p>
              </div>
              <div className="employee-details-title-bottom-container">
                <h1
                  id="employee-details-name"
                  className="common-top-panel-title"
                >
                  {user.name ? user.name : user.email}
                </h1>
                {user.name && (
                  <Image
                    id="employee-details-edit"
                    src="/icon/edit.svg"
                    onClick={this.onClickEdit}
                  />
                )}
              </div>
            </div>
            <div style={{ flexGrow: 1 }} />
            <Dropdown
              text={dropDownText}
              basic
              className="graph-date-drop-down"
              fluid
              icon={<div className="graph-date-drop-down-icon" />}
            >
              <Dropdown.Menu>
                {months.map(month => (
                  <Dropdown.Item
                    value={month}
                    onClick={(e, { value }) => {
                      if (this.state.graphDate !== value) {
                        dispatch(
                          employeeDetailsActions.fetchWorkLogs(user.uid, value),
                        );
                        this.setState({ graphDate: value });
                      }
                    }}
                    className="graph-date-drop-down-item"
                  >
                    {month === lifetime
                      ? lifetime
                      : moment(month).format('MMMM YYYY')}
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>
            <Image
              id="employee-details-export"
              className={
                filteredWorkLogs.length > 0
                  ? 'employee-details-export-enabled'
                  : 'employee-details-export-disabled'
              }
              src="/icon/export.png"
              disabled={filteredWorkLogs.length === 0}
              onClick={() => this.onClickExport(projectsMap, filteredWorkLogs)}
            />
          </div>
          <div className="employee-details-content">
            {this.renderEmployeeDetails(
              projectsMap,
              filteredWorkLogs,
              lifetimeStartDate,
              loggedProjects,
            )}
          </div>
        </Segment>
        <div className="common-side-panel">
          <div className="common-top-panel">
            <h1 id="team-title" className="common-side-panel-title">
              {Strings.EMPLOYEE_DETAILS.activeProjects}
            </h1>
          </div>
          <div className="employee-details-side-panel-content">
            {this.renderSidePanelContent()}
          </div>
        </div>
      </Segment>
    );
  }
}

EmployeeDetails.propTypes = {
  isProjectsLoading: PropTypes.bool.isRequired,
  isTeamMembersLoading: PropTypes.bool.isRequired,
  projects: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      color: PropTypes.string.isRequired,
      currencyCode: PropTypes.string.isRequired,
      status: PropTypes.string.isRequired,
    }),
  ).isRequired,
  workLogs: PropTypes.arrayOf(
    PropTypes.shape({
      userUid: PropTypes.string.isRequired,
      date: PropTypes.string.isRequired,
      totalLoggedMillis: PropTypes.number.isRequired,
    }),
  ).isRequired,
};

const mapStateToProps = state => ({
  isProjectsLoading: state.uiState.isProjectsLoading,
  isTeamMembersLoading: state.uiState.isTeamMembersLoading,
  projects: state.projects,
  workLogs: state.projectDetails,
});

export default connect(mapStateToProps)(EmployeeDetails);



// WEBPACK FOOTER //
// ./src/components/directory/EmployeeDetails.js